import { type IntlShape } from 'react-intl';
import { CampaignQuestion, Features } from 'types';
import { isFeatureGroupAndOptionEnabled } from 'utils/util/util';
import { SubTogglesKeys } from 'views/Widgets/Inputs/SwitchInputWithSubToggle';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';

export const AI_ASSISTED = 'aiAssisted';

export const getAiDraftQuestionInputConfig = (
  formatMessage: IntlShape['formatMessage']
) => {
  return [
    {
      type: INPUT_TYPES.SWITCH,
      name: AI_ASSISTED,
      label: formatMessage({
        id: 'app.views.activities.widgets.inputs.question_editor.inputs.ai_assisted.label',
        defaultMessage: 'AI Draft',
      }),
      helperHover: formatMessage({
        id: 'app.views.activities.widgets.inputs.question_editor.inputs.ai_assisted.help',
        defaultMessage:
          'If enabled, the employee will be able to use AI to draft an answer to this question.',
      }),
      defaultValue: true,
    },
  ];
};

export const isQuestionTypeDisallowedForAiAssisted = (
  uppercaseQuestionType: string
): boolean => {
  return [
    INPUT_TYPES.AUTOSUGGEST,
    INPUT_TYPES.BUTTON_OPTIONS,
    INPUT_TYPES.CONTACT_METHOD_EDITOR,
    INPUT_TYPES.CUSTOM_COMPONENT,
    INPUT_TYPES.DATE_PICKER,
    INPUT_TYPES.MEDIA_UPLOADER,
    INPUT_TYPES.MENTION_EDITOR,
    INPUT_TYPES.PEOPLE_EDITOR,
    INPUT_TYPES.PERCENTAGE_INPUT,
    INPUT_TYPES.QUESTIONS_EDITOR,
    INPUT_TYPES.SECTION,
    INPUT_TYPES.SELECT_PERSON,
    INPUT_TYPES.SWITCH_WITH_TIMEFRAME_SELECTOR,
    INPUT_TYPES.TABLE_EDITOR,
  ].includes(uppercaseQuestionType);
};

export const shouldShowAIAssistedQuestionDescriptor = (
  features: Features,
  featureToggleID: keyof Features['ai_assistant'],
  questionType: string,
  aiAssisted?: boolean | null,
  customQuestionsAIAssistedQuestionsGroupToggle?: boolean | null
): boolean => {
  if (
    // Organization feature disabled, for sure we don't show.
    !isFeatureGroupAndOptionEnabled(
      features,
      'ai_assistant',
      featureToggleID
    ) ||
    // Client manually disabled AI for the block.
    customQuestionsAIAssistedQuestionsGroupToggle === false ||
    // Client manually disabled AI for the question.
    aiAssisted === false
  ) {
    return false;
  }

  return !isQuestionTypeDisallowedForAiAssisted(
    questionType?.toUpperCase() ?? ''
  );
};

export const getAiAssistedValueForQuestionInCustomGuestionGroup = (
  customQuestionGroup: (CampaignQuestion & { AI_ASSISTED?: boolean })[],
  questionName: string
): boolean | null | undefined => {
  const question = customQuestionGroup.find(
    (question) => question.name === questionName
  );
  return question?.[AI_ASSISTED];
};

export const isQuestionPartOfCustomQuestionGroup = (
  customQuestionGroup: (CampaignQuestion & { AI_ASSISTED?: boolean })[],
  questionName: string
): boolean => {
  return customQuestionGroup.some((question) => question.name === questionName);
};

const nativeQuestionNameToCampaignAdminNameMapping = {
  rating: {
    name: 'hide_manager_rating',
    hasAiEnabled: (question) => question?.[SubTogglesKeys.SECONDARY_TOGGLE],
  },
  rating_comments: {
    name: 'hide_manager_rating',
    hasAiEnabled: (question) => question?.[SubTogglesKeys.SECONDARY_TOGGLE],
  },
};

export const isNativeQuestionAiEnabled = (questionName: string): boolean =>
  Object.keys(nativeQuestionNameToCampaignAdminNameMapping).includes(
    questionName
  );

export const getAiAssistedValueForNativeQuestion = (
  questionName: string,
  currentPhase: any
) => {
  const mapping = nativeQuestionNameToCampaignAdminNameMapping?.[questionName];
  return mapping?.hasAiEnabled(currentPhase?.[mapping?.name]);
};
